import React, { useState } from "react"
import { Tooltip } from "react-tooltip"
import { formatOperation } from "../helpers"
import { CustomState, CustomStateType, OrderCustomState } from "lib/types/generated/graphql-types"
import { ReactComponent as XMarkCircle } from "images/icons/circle-xmark.svg"

import {
  CustomStateComponent,
  CustomStateInfo,
  CustomStateWrapper,
  Operation,
  RecentState,
  State
} from "./OrderCustomStates.styled"
import Popup from "../../Ui/Popup"
import { useMutation } from "@apollo/client"
import { useSelector } from "react-redux"
import { orderLoading } from "lib/store/services/Order/orderSelector"
import ActionButton from "../../Ui/Table/ActionButton"
import { handleErrorMessages } from "helpers/errors"
import REMOVE_CUSTOM_STATE_FROM_ORDER from "../../../graphql/mutations/settings/customState/RemoveCustomStateFromOrder"

type Props = {
  customStates: OrderCustomState[]
  getCustomState: (id: string) => CustomState | undefined
  orderId: string
  orderRef: string
  removeCustomStateFromHistory: (id: string) => void
}

const OrderCustomStates = ({
  customStates,
  getCustomState,
  orderId,
  orderRef,
  removeCustomStateFromHistory
}: Props) => {
  const [showPopup, setShowPopup] = useState(false)
  const orderSelector = useSelector(orderLoading)

  const [removeCustomState, { loading }] = useMutation(REMOVE_CUSTOM_STATE_FROM_ORDER, {
    onCompleted: (result) => {
      removeCustomStateFromHistory(result.Order.removeCustomState)
    },
    onError: (e) => {
      handleErrorMessages(e)
    }
  })

  const removeCustomStateFromOrder = (id: string) => {
    removeCustomState({ variables: { orderId: orderId, stateId: id } })
  }

  const customStatesReversed = [...customStates].reverse()
  const recentState = customStatesReversed[0]

  return (
    <CustomStateWrapper>
      {showPopup && (
        <Popup
          title={"Remove custom states"}
          subtitle={`Remove custom states from order #${orderRef}`}
          handleCloseClick={() => setShowPopup(false)}
          hideOkButton={true}
        >
          <div>
            {customStatesReversed.map((state) => (
              <State key={state.id} className={loading ? "blur" : ""}>
                {getCustomState(state.id)?.description}{" "}
                <ActionButton
                  mode={"delete"}
                  handleOnClick={() => removeCustomStateFromOrder(state.id)}
                />
              </State>
            ))}
          </div>
        </Popup>
      )}
      {recentState && getCustomState(recentState?.id)?.description && (
        <RecentState onClick={() => setShowPopup(true)}>
          <CustomStateComponent
            key={recentState.id}
            $blockingState={recentState.type === CustomStateType.Blocking}
            className={orderSelector ? "blur" : ""}
          >
            {recentState?.operations && recentState?.operations?.length > 0 && (
              <Tooltip id={`${recentState.id}`}>
                <div>
                  Blocking operations:{" "}
                  {recentState?.operations?.map((operation) => (
                    <Operation key={operation}>
                      <XMarkCircle />
                      {formatOperation(operation)}
                    </Operation>
                  ))}
                </div>
              </Tooltip>
            )}
            <CustomStateInfo data-tooltip-id={`${recentState.id}`} data-tooltip-delay-hide={100}>
              {getCustomState(recentState?.id)?.description ?? recentState?.id}
            </CustomStateInfo>
          </CustomStateComponent>
        </RecentState>
      )}
    </CustomStateWrapper>
  )
}

export default OrderCustomStates
